import { useEffect } from 'react'
import { Portal, Box, Text, useColorModeValue } from '@chakra-ui/react'
import { MousePointerClick } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'

export interface RowHoverHintState {
  visible: boolean
  top: number
  left: number
}

export const CLOSED_ROW_HOVER_HINT: RowHoverHintState = {
  visible: false,
  top: 0,
  left: 0,
}

interface RowActionHoverHintProps {
  hintState: RowHoverHintState
  scrollContainerRef?: React.RefObject<HTMLElement | null>
  onHide?: () => void
}

export function RowActionHoverHint({ hintState, scrollContainerRef, onHide }: RowActionHoverHintProps) {
  const colors = useThemeColors()
  const bg = colors.brandBlue
  const bgHover = colors.brandBlueHover
  const textColor = useColorModeValue('white', 'white')
  const shadow = useColorModeValue('0 4px 14px rgba(32, 91, 243, 0.45)', '0 4px 14px rgba(59, 130, 246, 0.5)')

  useEffect(() => {
    if (!hintState.visible || !onHide) return

    const scrollEl = scrollContainerRef?.current
    const handleScroll = () => onHide()

    scrollEl?.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollEl?.removeEventListener('scroll', handleScroll)
  }, [hintState.visible, onHide, scrollContainerRef])

  if (!hintState.visible) return null

  return (
    <Portal>
      <Box
        data-row-action-hint
        position="fixed"
        top={`${hintState.top}px`}
        left={`${hintState.left}px`}
        zIndex={1500}
        pointerEvents="none"
        transform="translateY(-100%)"
        mt="-6px"
      >
        <Box
          display="inline-flex"
          alignItems="center"
          gap={2}
          bg={bg}
          color={textColor}
          px={3}
          py={1.5}
          borderRadius="md"
          boxShadow={shadow}
          borderWidth="1px"
          borderColor={bgHover}
          maxW="none"
          whiteSpace="nowrap"
        >
          <Box as="span" display="inline-flex" flexShrink={0} opacity={0.95}>
            <MousePointerClick size={14} />
          </Box>
          <Text as="span" fontSize="xs" fontWeight="bold" letterSpacing="wide" textTransform="uppercase">
            Right-click for actions
          </Text>
        </Box>
        <Box
          position="absolute"
          bottom="-5px"
          left="16px"
          w={0}
          h={0}
          borderLeft="6px solid transparent"
          borderRight="6px solid transparent"
          borderTop={`6px solid ${bg}`}
        />
      </Box>
    </Portal>
  )
}
